import { ChatChannel, ChatEvent, ChatMessage, ChatUnreadInfo } from '../api';
import { mentionsLogin } from '../lib/mentions';

/*
 * Список чатов как состояние, а не как снимок.
 *
 * Раньше список правился только приходом сообщения, поэтому прочтение и
 * присутствие в нём тихо устаревали: в переписке уже две галочки, а слева
 * всё ещё одна; в шапке «давно не заходил», хотя человек в сети. Все правки
 * списка от событий потока собраны здесь — отдельно от компонента, чтобы
 * их можно было проверить тестом, а не глазами на живом сервере.
 */

export type ListCtx = {
  myId: string;
  /** Логин для разбора «@»: сервер живое упоминание в ленте отдельным событием не шлёт. */
  myLogin: string;
  /** Чат открыт на экране и вкладка в фокусе — сообщение прочитано сразу. */
  seenNow: (channelId: string) => boolean;
};

/** Строка предпросмотра под названием чата. */
export function previewOf(msg: ChatMessage): string {
  if (msg.text) return msg.text;
  if (msg.call) return msg.call.kind === 'video' ? 'Видеозвонок' : 'Звонок';
  if (msg.files.length > 1) return `${msg.files.length} вложения`;
  return msg.files[0] ? msg.files[0].name : '';
}

/**
 * Метка системного уведомления относится к чату, где всё прочитано: такое
 * уведомление пора закрыть. Метки звонков и неизвестных чатов не трогаем.
 */
export function isReadChatTag(tag: string, list: ChatChannel[]): boolean {
  if (!tag.startsWith('chat-')) return false;
  const id = tag.slice('chat-'.length);
  return list.some((c) => c.id === id && c.unread === 0);
}

export function applyEvent(list: ChatChannel[], e: ChatEvent, ctx: ListCtx): ChatChannel[] {
  if (e.type === 'message') return withMessage(list, e.channelId, e.data, ctx);
  if (e.type === 'message.updated') return withEdited(list, e.channelId, e.data);
  if (e.type === 'read') return withRead(list, e.channelId, e.data, ctx);
  if (e.type === 'unread') return withUnread(list, e.channelId, e.data);
  if (e.type === 'presence') return withPresence(list, e.data);
  return list;
}

/*
 * Остаток считает сервер после каждой отметки и после каждого ответа в моей
 * ветке (спеки 2026-10-02-tabs-devices-sync и 2026-11-04-chat-dorabotki):
 * ставим как есть все поля — число, отсечку, ветку, упоминания, — не выводя
 * их из прочитанного места самостоятельно. Новое в моей ветке сервер уже
 * вернул из скрытых; список повторяет это, иначе чат вернулся бы только после
 * перезагрузки. Прочтение (остаток ноль) скрытый чат не возвращает.
 */
export function withUnread(list: ChatChannel[], channelId: string, info: ChatUnreadInfo): ChatChannel[] {
  return list.map((c) =>
    c.id === channelId
      ? {
          ...c,
          unread: info.unread,
          firstUnreadId: info.firstUnreadId,
          threadRootId: info.threadRootId,
          mentions: info.mentions,
          mention: info.mention,
          hidden: info.unread > 0 ? false : c.hidden,
        }
      : c,
  );
}

/*
 * Правка последнего сообщения меняет и строку под названием чата: раньше там
 * оставался старый текст, пока не придёт следующее сообщение. Правку любого
 * другого сообщения список не замечает — она в строку не попадает.
 */
function withEdited(list: ChatChannel[], channelId: string, msg: ChatMessage): ChatChannel[] {
  return list.map((c) =>
    c.id === channelId && c.last && c.last.id === msg.id
      ? { ...c, last: { ...c.last, text: previewOf(msg) } }
      : c,
  );
}

function withMessage(
  list: ChatChannel[],
  channelId: string,
  msg: ChatMessage,
  ctx: ListCtx,
): ChatChannel[] {
  /*
   * Ответ в ветке список не трогает. Число на чате растят только ответы в
   * моих ветках — какие ветки мои, знает сервер, и он же присылает пересчёт
   * событием `unread`. Порядок в списке и скрытие ответ тоже не меняет: на
   * сервере он не двигает время чата, а скрытие снимает лишь участникам ветки.
   */
  if (msg.parentId) return list;
  const own = msg.author.id === ctx.myId;
  const seen = own || ctx.seenNow(channelId);
  const mentioned = !seen && mentionsLogin(msg.text, ctx.myLogin);
  return list.map((c) =>
    c.id === channelId
      ? {
          ...c,
          last: {
            id: msg.id,
            text: previewOf(msg),
            authorName: own ? 'Вы' : msg.author.name,
            mine: own,
            ts: msg.ts,
            // только что отправленное ещё никто не читал
            seenByAll: false,
          },
          lastMessageAt: msg.ts,
          unread: seen ? c.unread : c.unread + 1,
          // сервер `unread` на сообщения ленты не шлёт: значок «@» и границу
          // «новые» (чат откроется с неё, а не с запомненного места) ведём сами
          firstUnreadId: seen ? c.firstUnreadId : (c.firstUnreadId ?? msg.id),
          mentions: mentioned ? c.mentions + 1 : c.mentions,
          mention: mentioned ? (c.mention ?? { id: msg.id, rootId: null }) : c.mention,
          // сервер снимает скрытие вместе с новым сообщением (messages.service),
          // и список обязан это повторить — иначе чат вернётся только после
          // полной перезагрузки, хотя обещано «вернётся с новым сообщением»
          hidden: false,
        }
      : c,
  );
}

function withRead(
  list: ChatChannel[],
  channelId: string,
  data: { userId: string; messageId: number; seenByAll: boolean },
  ctx: ListCtx,
): ChatChannel[] {
  return list.map((c) => {
    if (c.id !== channelId) return c;
    // прочитал я сам — на телефоне или в соседней вкладке; счётчик гасим
    // только если дочитано до известного нам последнего сообщения
    const readToEnd = data.userId === ctx.myId && !!c.last && data.messageId >= c.last.id;
    return {
      ...c,
      unread: readToEnd ? 0 : c.unread,
      // галочки рисуются только у своих сообщений — чужие не трогаем
      last: c.last?.mine ? { ...c.last, seenByAll: data.seenByAll } : c.last,
    };
  });
}

function withPresence(
  list: ChatChannel[],
  data: { userId: string; online: boolean; lastSeen: number },
): ChatChannel[] {
  // присутствие приходит обо всех, кто в сети, а лички есть не со всеми:
  // посторонний — тот же список, без перерисовки
  if (!list.some((c) => c.kind === 'dm' && c.peer?.id === data.userId)) return list;
  return list.map((c) =>
    c.kind === 'dm' && c.peer?.id === data.userId
      ? {
          ...c,
          peerOnline: data.online,
          // пока человек в сети, прежнее время визита ещё пригодится: оно
          // станет подписью в тот момент, когда он закроет последнюю вкладку
          peer: data.online ? c.peer : { ...c.peer, lastSeen: data.lastSeen },
        }
      : c,
  );
}
